const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

export interface AuditItem {
  id?: number;
  category_id: number;
  name: string;
  status: 'exists' | 'missing' | 'outdated';
  priority: 'high' | 'medium' | 'low';
  cost?: string;
  cost_type?: 'monthly' | 'one-time' | 'percentage';
  notes?: string;
}

export interface AuditCategory {
  id: number;
  name: string;
  description?: string;
  items: AuditItem[];
}

export interface Phase {
  id: number;
  phase_number: number;
  name: string;
  goal: string;
  tech_focus: string;
  timeframe: string;
  status: 'not-started' | 'in-progress' | 'completed';
  completed_tasks: number;
  total_tasks: number;
  tasks?: PhaseTask[];
}

export interface PhaseTask {
  id: number;
  phase_id: number;
  title: string;
  description?: string;
  status: 'pending' | 'in-progress' | 'completed' | 'blocked';
  assigned_to?: string;
  due_date?: string;
}

export interface SystemStatus {
  id: number;
  name: string;
  category?: string;
  status: 'online' | 'degraded' | 'offline';
  uptime_percentage: number;
  health_score?: number;
  last_check: string;
}

export interface AutomationWorkflow {
  id: number;
  name: string;
  category?: string;
  trigger_type?: string;
  trigger_description?: string;
  actions?: string[];
  status: 'active' | 'paused' | 'draft' | 'archived';
  frequency?: string;
  impact_level?: 'critical' | 'high' | 'medium' | 'low';
  run_count: number;
  time_saved_minutes: number;
  last_run?: string;
}

export interface GuestMetrics {
  date: string;
  occupancy_rate: number;
  revpar: number;
  adr: number;
  guest_satisfaction: number;
  nps_score: number;
  direct_booking_percentage: number;
  digital_checkin_percentage: number;
  avg_response_time_minutes: number;
  total_revenue: number;
  ancillary_revenue: number;
}

export interface QRTouchpoint {
  id: number;
  location: string;
  purpose: string;
  scan_count: number;
  last_scan?: string;
  active: boolean;
}

class CTODashboardAPI {
  // ============= AUDIT METHODS =============
  
  async getAuditCategories(): Promise<AuditCategory[]> {
    const response = await fetch(`${API_BASE}/cto/audit/categories`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.data;
  }

  async getAuditSummary() {
    const response = await fetch(`${API_BASE}/cto/audit/summary`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.data;
  }

  async createAuditItem(item: Omit<AuditItem, 'id'>): Promise<number> {
    const response = await fetch(`${API_BASE}/cto/audit/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item)
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.id;
  }

  async updateAuditItem(id: number, item: Partial<AuditItem>): Promise<void> {
    const response = await fetch(`${API_BASE}/cto/audit/items/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item)
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
  }

  async deleteAuditItem(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/cto/audit/items/${id}`, {
      method: 'DELETE'
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
  }

  // ============= PHASE METHODS =============
  
  async getPhases(): Promise<Phase[]> {
    const response = await fetch(`${API_BASE}/cto/phases`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.data;
  }

  async updatePhaseStatus(id: number, status: Phase['status']): Promise<void> {
    const response = await fetch(`${API_BASE}/cto/phases/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
  }

  async createPhaseTask(phaseId: number, task: Omit<PhaseTask, 'id' | 'phase_id'>): Promise<number> {
    const response = await fetch(`${API_BASE}/cto/phases/${phaseId}/tasks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(task)
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.id;
  }

  async updateTaskStatus(id: number, status: PhaseTask['status']): Promise<void> {
    const response = await fetch(`${API_BASE}/cto/tasks/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
  }

  // ============= SYSTEM STATUS METHODS =============
  
  async getSystemStatuses(): Promise<SystemStatus[]> {
    const response = await fetch(`${API_BASE}/cto/systems/status`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.data;
  }

  async updateSystemStatus(
    id: number, 
    status: SystemStatus['status'], 
    uptime?: number, 
    responseTime?: number
  ): Promise<void> {
    const response = await fetch(`${API_BASE}/cto/systems/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        status, 
        uptime_percentage: uptime, 
        response_time_ms: responseTime 
      })
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
  }

  async logSystemIncident(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/cto/systems/${id}/incidents`, {
      method: 'POST'
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
  }

  // ============= AUTOMATION METHODS =============
  
  async getAutomations(category?: string): Promise<AutomationWorkflow[]> {
    const url = category 
      ? `${API_BASE}/cto/automations?category=${encodeURIComponent(category)}`
      : `${API_BASE}/cto/automations`;
    const response = await fetch(url);
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.data;
  }

  async createAutomation(workflow: Omit<AutomationWorkflow, 'id' | 'run_count' | 'time_saved_minutes'>): Promise<number> {
    const response = await fetch(`${API_BASE}/cto/automations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(workflow)
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.id;
  }

  async toggleAutomation(id: number): Promise<'active' | 'paused'> {
    const response = await fetch(`${API_BASE}/cto/automations/${id}/toggle`, {
      method: 'PUT'
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.status;
  }

  async logAutomationRun(id: number, success: boolean, timeSaved?: number): Promise<void> {
    const response = await fetch(`${API_BASE}/cto/automations/${id}/runs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ success, time_saved_minutes: timeSaved })
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
  }

  // ============= GUEST METRICS METHODS =============
  
  async getGuestMetrics(startDate?: string, endDate?: string): Promise<GuestMetrics[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const url = `${API_BASE}/cto/metrics/guest${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url);
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.data;
  }

  async recordGuestMetrics(metrics: GuestMetrics): Promise<void> {
    const response = await fetch(`${API_BASE}/cto/metrics/guest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(metrics)
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
  }

  // ============= AI CONCIERGE METHODS =============
  
  async logAIQuery(query: {
    query_text: string;
    query_category?: string;
    language?: string;
    response_time_ms?: number;
    automated?: boolean;
    resolution_status?: 'resolved' | 'escalated' | 'failed';
    guest_rating?: number;
    touchpoint_location?: string;
  }): Promise<number> {
    const response = await fetch(`${API_BASE}/cto/ai/queries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(query)
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.id;
  }

  async getAIAnalytics() {
    const response = await fetch(`${API_BASE}/cto/ai/analytics`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.data;
  }

  // ============= QR TOUCHPOINT METHODS =============
  
  async getQRTouchpoints(): Promise<QRTouchpoint[]> {
    const response = await fetch(`${API_BASE}/cto/qr/touchpoints`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.data;
  }

  async recordQRScan(location: string): Promise<void> {
    const response = await fetch(`${API_BASE}/cto/qr/scan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ location })
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
  }

  // ============= STAKEHOLDER FEEDBACK METHODS =============
  
  async recordStakeholderFeedback(feedback: {
    stakeholder_role: string;
    feedback_text?: string;
    pain_points?: string;
    priority_request?: string;
    satisfaction_level?: number;
    interview_date?: string;
    follow_up_required?: boolean;
  }): Promise<number> {
    const response = await fetch(`${API_BASE}/cto/feedback/stakeholder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(feedback)
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.id;
  }

  async getStakeholderFeedback() {
    const response = await fetch(`${API_BASE}/cto/feedback/stakeholder/summary`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.data;
  }

  // ============= VENDOR MANAGEMENT METHODS =============
  
  async getVendors(category?: string) {
    const url = category 
      ? `${API_BASE}/cto/vendors?category=${encodeURIComponent(category)}`
      : `${API_BASE}/cto/vendors`;
    const response = await fetch(url);
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.data;
  }

  async saveVendor(vendor: any): Promise<number> {
    const response = await fetch(`${API_BASE}/cto/vendors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(vendor)
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error);
    return data.id;
  }
}

export const ctoDashboardApi = new CTODashboardAPI();